import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api";
import { toYouTubeEmbed } from "../youtube";

export default function CoursePlayer() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api
      .courses.get(id)
      .then((c) => {
        setCourse(c);
        if (!activeLessonId && c.lessons.length > 0) setActiveLessonId(c.lessons[0].id);
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!course) return <div className="text-gray-500">Loading…</div>;

  const activeLesson = course.lessons.find((l) => l.id === activeLessonId);

  const selectLesson = (lesson) => {
    if (lesson.locked) return;
    setActiveLessonId(lesson.id);
    setAnswers({});
    setResult(null);
  };

  const markWatched = async () => {
    try {
      await api.progress.completeLesson(activeLesson.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const submitQuiz = async () => {
    const answerArray = activeLesson.quiz.map((_, i) => answers[i]);
    if (answerArray.some((a) => a === undefined)) {
      setError("Please answer every question before submitting.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await api.progress.submitQuiz(activeLesson.id, answerArray);
      setResult(res);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const embedUrl = activeLesson ? toYouTubeEmbed(activeLesson.video_url) : "";
  const hasQuiz = activeLesson && activeLesson.quiz && activeLesson.quiz.length > 0;
  const lessonPassed = activeLesson?.progress?.passed === 1;

  return (
    <div>
      <Link to="/" className="text-sm hover:underline" style={{ color: "#154E93" }}>← Back to My Training</Link>
      <h1 className="text-2xl font-bold mt-2 mb-4" style={{ color: "#154E93" }}>{course.title}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm p-3 space-y-1 h-fit">
          {course.lessons.map((l, i) => {
            const passed = l.progress?.passed === 1;
            return (
              <button
                key={l.id}
                onClick={() => selectLesson(l)}
                disabled={l.locked}
                className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${
                  l.locked ? "opacity-40 cursor-not-allowed" : l.id === activeLessonId ? "bg-teal-50 font-semibold" : "hover:bg-gray-50"
                }`}
                style={l.id === activeLessonId && !l.locked ? { color: "#154E93" } : {}}
              >
                <span>{i + 1}. {l.title}</span>
                {passed && <span className="text-green-600 ml-1">✓</span>}
                {l.locked && <span className="ml-1" title="Locked — complete earlier lessons first">🔒</span>}
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-3 bg-white rounded-lg shadow-sm p-5">
          {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

          {activeLesson && (
            <>
              <h2 className="text-lg font-bold mb-3">{activeLesson.title}</h2>

              {embedUrl && (
                <div className="mb-4 aspect-video w-full">
                  <iframe
                    src={embedUrl}
                    title={activeLesson.title}
                    className="w-full h-full rounded"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {activeLesson.written_content && (
                <div className="prose prose-sm max-w-none mb-5 whitespace-pre-wrap text-gray-700">
                  {activeLesson.written_content}
                </div>
              )}

              {!hasQuiz && !lessonPassed && (
                <button
                  onClick={markWatched}
                  className="px-4 py-2 text-white rounded text-sm font-medium"
                  style={{ backgroundColor: "#154E93" }}
                >
                  Mark as Complete
                </button>
              )}
              {!hasQuiz && lessonPassed && (
                <div className="text-sm text-green-600 font-medium">✓ Completed</div>
              )}

              {hasQuiz && (
                <div className="border-t pt-4 mt-2">
                  <h3 className="font-semibold mb-3">Knowledge Check</h3>
                  {activeLesson.quiz.map((q, qi) => (
                    <div key={qi} className="mb-4">
                      <div className="text-sm font-medium mb-2">{qi + 1}. {q.question}</div>
                      <div className="space-y-1">
                        {q.choices.map((choice, ci) => (
                          <label key={ci} className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name={`q-${qi}`}
                              checked={answers[qi] === ci}
                              onChange={() => setAnswers({ ...answers, [qi]: ci })}
                            />
                            {choice}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={submitQuiz}
                    disabled={submitting}
                    className="px-4 py-2 text-white rounded text-sm font-medium"
                    style={{ backgroundColor: "#F3850C" }}
                  >
                    {submitting ? "Submitting…" : "Submit Answers"}
                  </button>

                  {result && (
                    <div className={`mt-4 text-sm px-3 py-2 rounded ${result.passed ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                      {result.passed
                        ? `✓ Passed — ${result.correct}/${result.total} correct (${Math.round(result.score * 100)}%)`
                        : `Not quite — ${result.correct}/${result.total} correct (${Math.round(result.score * 100)}%). You need 70% to pass. Try again.`}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
